import { useEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ATTENTION_KEY, useAttention } from '../services/attention';
import { Symbol } from '../components/symbol';
import { BrandLogo, BRAND_NAME } from '../components/brand-logo';
import { useAuthStore } from '../store/auth-store';

export interface NavItem {
  href: string;
  label: string;
  /** Material Symbols Rounded ligature name. */
  icon: string;
  scope?: string;
  role?: string;
  /**
   * Things that need action now (design system v2 §04b: "counts appear only
   * for things that need action"). Nothing feeds this yet — the shell has no
   * counts endpoint — but the sidebar renders it when set.
   */
  count?: number;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

// Modules grouped by job, per design system v2 §04b. Group headings and labels
// are sentence case.
export const NAV: NavSection[] = [
  {
    label: 'Overview',
    items: [{ href: '/dashboard', label: 'Dashboard', icon: 'grid_view' }],
  },
  {
    label: 'Commerce',
    items: [
      { href: '/orders', label: 'Orders', icon: 'shopping_bag' },
      { href: '/products', label: 'Products', icon: 'inventory_2' },
      { href: '/customers', label: 'Customers', icon: 'group' },
      { href: '/sellers', label: 'Sellers', icon: 'storefront' },
      { href: '/delivery', label: 'Delivery', icon: 'local_shipping' },
    ],
  },
  {
    label: 'Content',
    items: [
      { href: '/catalog', label: 'Catalog', icon: 'category', scope: 'catalog.category.read' },
      { href: '/reviews', label: 'Reviews', icon: 'reviews', scope: 'catalog.review.moderate' },
      { href: '/notifications', label: 'Notifications', icon: 'campaign', scope: 'notification.broadcast.send' },
      // Help & support details, privacy policy and terms the apps show.
      { href: '/app-content', label: 'App content', icon: 'article', scope: 'config.write' },
    ],
  },
  {
    label: 'Support',
    items: [
      { href: '/support', label: 'Tickets', icon: 'support_agent', scope: 'support.read' },
      { href: '/communication-logs', label: 'Communication log', icon: 'forum', scope: 'audit.read' },
    ],
  },
  {
    label: 'Finance',
    items: [
      { href: '/reports', label: 'Reports', icon: 'bar_chart', scope: 'report.financial' },
      { href: '/payouts', label: 'Payouts', icon: 'account_balance_wallet', scope: 'payout.read' },
      { href: '/refunds', label: 'Refunds', icon: 'currency_exchange', scope: 'payout.read' },
    ],
  },
  {
    label: 'Monitoring',
    items: [
      { href: '/error-logs', label: 'Error log', icon: 'bug_report', scope: 'audit.read' },
      { href: '/audit-trail', label: 'Audit trail', icon: 'history', scope: 'audit.read' },
    ],
  },
  {
    label: 'System',
    items: [
      // One settings destination. Fees, sign-in, delivery, media, catalog and
      // every gateway credential are all groups on this page.
      { href: '/integrations', label: 'Platform config', icon: 'tune', scope: 'config.write' },
      { href: '/sms-templates', label: 'SMS templates', icon: 'sms', scope: 'config.write' },
      // The dropdown/chip vocabularies the apps render. Next to Platform Config
      // because both answer "why does the app show that option" — one for values
      // and credentials, one for lists.
      { href: '/masters', label: 'Master data', icon: 'list_alt', scope: 'master.read' },
      { href: '/admins', label: 'Admins', icon: 'shield_person', role: 'SUPER_ADMIN' },
      { href: '/settings', label: 'Settings', icon: 'settings' },
    ],
  },
];

function initialsFromRole(role: string): string {
  return role
    .split('_')
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

/** `SUPER_ADMIN` → `Super admin`. */
function prettyRole(role: string): string {
  const s = role.replace(/_/g, ' ').toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Top-right account button: initials avatar that opens a small menu with the
 * signed-in role and Sign out. Closes on outside click and Escape, returning
 * focus to the avatar.
 */
function AccountMenu({ role, onLogout }: { role: string; onLogout: () => void }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative shrink-0">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account"
        title={prettyRole(role)}
        className="grid h-9 w-9 place-items-center rounded-full bg-shell text-[13px] font-bold text-white ring-2 ring-transparent transition-shadow hover:ring-surface-line focus-visible:ring-shell/40"
      >
        {initialsFromRole(role)}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-[calc(100%+8px)] z-30 w-56 overflow-hidden rounded-md border border-surface-line bg-surface-card py-1 shadow-elev-3 motion-safe:animate-fadeIn"
        >
          <div className="border-b border-surface-line px-3.5 py-2.5">
            <div className="text-[11.5px] text-ink-500">Signed in as</div>
            <div className="truncate text-[14px] font-semibold text-ink-900">{prettyRole(role)}</div>
          </div>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
            className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-[14px] text-ink-700 transition-colors hover:bg-surface-2 hover:text-ink-900"
          >
            <Symbol name="logout" size={18} />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

const COLLAPSE_KEY = 'utsaaha.admin.sidebarCollapsed';

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Whether [item] is shown to the signed-in admin. Shared with the dashboard's
 * quick links so both agree on what a role can reach.
 */
export function isNavItemVisible(
  item: NavItem,
  hasScope: (key: string) => boolean,
  hasAnyRole: (...roles: string[]) => boolean,
): boolean {
  if (item.role && !hasAnyRole(item.role)) return false;
  if (item.scope && !hasScope(item.scope)) return false;
  return true;
}

/**
 * Admin shell — design system v2 §04 "App shell":
 *
 * | Width     | Navigation                                                     |
 * |-----------|----------------------------------------------------------------|
 * | `< 900px` | off-canvas drawer behind a hamburger, page fills the width     |
 * | `>= 900`  | jamun-800 sidebar, 248px expanded or 68px collapsed (the user's |
 * |           | choice, remembered per browser); collapsed shows tooltips      |
 *
 * Top bar is 60px, white, with a hairline; the work area is ink-50 with a 32px
 * gutter and a 1440px max width.
 *
 * The drawer is a real dialog: it closes on Escape, on backdrop click and on
 * navigation, locks background scroll while open, and returns focus to the
 * trigger — the things a hand-rolled `hidden` toggle usually misses.
 */
export function AdminLayout() {
  const { pathname } = useLocation();
  const session = useAuthStore((s) => s.session);
  const hasScope = useAuthStore((s) => s.hasScope);
  const hasAnyRole = useAuthStore((s) => s.hasAnyRole);
  const logout = useAuthStore((s) => s.logout);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  // Work waiting on this admin, as count badges on the matching menu items.
  const attention = useAttention();
  const queryClient = useQueryClient();
  // Moving between pages usually means something was just approved or
  // handled, so re-count rather than wait for the next poll.
  useEffect(() => {
    void queryClient.invalidateQueries({ queryKey: ATTENTION_KEY });
  }, [pathname, queryClient]);
  const byNav = attention.data?.byNav ?? {};

  const visibleSections = NAV.map((section) => ({
    ...section,
    items: section.items
      .filter((item) => isNavItemVisible(item, hasScope, hasAnyRole))
      .map((item) => (byNav[item.href] ? { ...item, count: byNav[item.href] } : item)),
  })).filter((section) => section.items.length > 0);

  const currentItem = NAV.flatMap((s) => s.items).find((i) => pathname.startsWith(i.href));
  const currentLabel = currentItem?.label ?? 'Dashboard';
  const currentGroup = NAV.find((s) => s.items.includes(currentItem as NavItem))?.label;

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      const next = !c;
      try {
        localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0');
      } catch {
        /* private mode — the choice just isn't remembered */
      }
      return next;
    });
  };

  // Navigating from inside the drawer should dismiss it, and focus belongs back
  // on the control that opened it so keyboard users are not dropped at the top
  // of the document.
  useEffect(() => {
    setDrawerOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!drawerOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setDrawerOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    // Stop the page behind the overlay scrolling under the user's finger.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [drawerOpen]);

  return (
    // Lock the shell to the viewport so the sidebar and main content scroll
    // independently (overflow-hidden here stops the whole document scrolling).
    <div className="flex h-[100dvh] overflow-hidden bg-surface-app text-ink-900">
      {/* Persistent sidebar — 900px and up. */}
      <aside
        className={[
          'on-shell hidden shrink-0 flex-col bg-shell text-shell-on min-[900px]:flex',
          'transition-[width] duration-std ease-brand',
          collapsed ? 'w-sidebar-collapsed' : 'w-sidebar',
        ].join(' ')}
      >
        <SidebarContent
          sections={visibleSections}
          pathname={pathname}
          session={session}
          onLogout={() => void logout()}
          collapsed={collapsed}
        />
      </aside>

      {/* Off-canvas drawer — below 900px. */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 min-[900px]:hidden">
          <button
            type="button"
            aria-label="Close navigation"
            onClick={() => setDrawerOpen(false)}
            className="absolute inset-0 h-full w-full cursor-default bg-scrim motion-safe:animate-fadeIn"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Main navigation"
            className="on-shell absolute inset-y-0 left-0 flex w-[min(280px,85vw)] flex-col bg-shell text-shell-on shadow-elev-4 motion-safe:animate-slideIn"
          >
            <SidebarContent
              sections={visibleSections}
              pathname={pathname}
              session={session}
              onLogout={() => void logout()}
              onClose={() => setDrawerOpen(false)}
            />
          </div>
        </div>
      )}

      {/* Content column. */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-10 flex h-topbar shrink-0 items-center gap-3 border-b border-surface-line bg-surface-card px-4 min-[900px]:px-5">
          <button
            ref={menuButtonRef}
            type="button"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open navigation"
            aria-expanded={drawerOpen}
            className="btn btn-quiet btn-icon -ml-1 min-[900px]:hidden"
          >
            <Symbol name="menu" size={22} />
          </button>
          <button
            type="button"
            onClick={toggleCollapsed}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-pressed={collapsed}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="btn btn-quiet btn-icon -ml-1 hidden text-ink-500 min-[900px]:inline-flex"
          >
            <Symbol name={collapsed ? 'left_panel_open' : 'left_panel_close'} size={20} />
          </button>

          {/* Breadcrumb collapses to just the current page on small screens —
              "Utsaaha › Orders" is not worth 90px of a 360px header. */}
          <nav aria-label="Breadcrumb" className="min-w-0">
            <ol className="flex min-w-0 items-center gap-2 text-[14px] text-ink-500">
              <li className="hidden sm:block">{BRAND_NAME}</li>
              {currentGroup && currentGroup !== 'Overview' && (
                <>
                  <li aria-hidden className="hidden items-center sm:flex">
                    <Symbol name="chevron_right" size={14} />
                  </li>
                  <li className="hidden sm:block">{currentGroup}</li>
                </>
              )}
              <li aria-hidden className="hidden items-center sm:flex">
                <Symbol name="chevron_right" size={14} />
              </li>
              <li aria-current="page" className="truncate font-semibold text-ink-900">
                {currentLabel}
              </li>
            </ol>
          </nav>
          {/* No global search here: every list page has its own search above
              its table, scoped to that table's columns. */}

          {/* Brand mark, top right on every page. Links home. */}
          <Link
            to="/dashboard"
            aria-label={`${BRAND_NAME} — dashboard`}
            title={BRAND_NAME}
            className="ml-auto flex shrink-0 items-center rounded-md p-1 transition-opacity hover:opacity-85"
          >
            <BrandLogo height={28} />
          </Link>

          {session && <AccountMenu role={session.role} onLogout={() => void logout()} />}
        </header>

        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-content px-4 py-6 sm:px-6 min-[900px]:px-8 min-[900px]:py-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

interface SidebarContentProps {
  sections: NavSection[];
  pathname: string;
  session: { role: string; actorType: string } | null;
  onLogout: () => void;
  /** 68px icon rail with hover/focus tooltips (persistent sidebar only). */
  collapsed?: boolean;
  onClose?: () => void;
}

/**
 * Shared between the persistent sidebar and the mobile drawer, so the two can
 * never drift.
 *
 * Collapsed-mode tooltips are positioned `fixed` from the hovered link's rect:
 * the nav list scrolls, and an absolutely-positioned tooltip inside it would
 * be clipped by that scroll container.
 */
function SidebarContent({
  sections,
  pathname,
  session,
  onLogout,
  collapsed = false,
  onClose,
}: SidebarContentProps) {
  const [tip, setTip] = useState<{ label: string; top: number } | null>(null);

  const showTip = (label: string) => (e: { currentTarget: HTMLElement }) => {
    if (!collapsed) return;
    const r = e.currentTarget.getBoundingClientRect();
    setTip({ label, top: r.top + r.height / 2 });
  };
  const hideTip = () => setTip(null);

  return (
    <>
      {/* Brand block — the Utsaaha wordmark and the console name. */}
      <div
        className={[
          'mx-2.5 mb-2.5 flex shrink-0 items-center gap-2.5 border-b border-white/[0.08] pb-4 pt-[18px]',
          collapsed ? 'justify-center' : 'px-2',
        ].join(' ')}
      >
        {collapsed ? (
          <BrandLogo height={11} />
        ) : (
          <div className="min-w-0">
            <BrandLogo height={30} />
            <div className="mt-2 text-[11.5px] leading-none text-shell-muted">Admin console</div>
          </div>
        )}
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close navigation"
            className="ml-auto grid h-9 w-9 place-items-center rounded-sm text-shell-muted transition-colors hover:bg-white/[0.06] hover:text-shell-on"
          >
            <Symbol name="close" size={20} />
          </button>
        )}
      </div>

      <nav
        aria-label="Modules"
        className="scrollbar-hide flex-1 overflow-y-auto px-2.5 pb-3"
        onScroll={hideTip}
      >
        {sections.map((section, si) => (
          <div key={section.label}>
            {collapsed ? (
              // Group headings are hidden at rail width, so a hairline keeps
              // the groups legible as groups instead of one long list.
              si > 0 && <div className="mx-auto my-2.5 h-px w-6 bg-white/[0.12]" aria-hidden />
            ) : (
              <div className="px-2.5 pb-1.5 pt-3 text-[12px] font-medium text-shell-muted">
                {section.label}
              </div>
            )}
            <div className="flex flex-col gap-0.5">
              {section.items.map(({ href, label, icon, count }) => {
                const active = pathname.startsWith(href);
                return (
                  <Link
                    key={href}
                    to={href}
                    aria-label={collapsed ? label : undefined}
                    aria-current={active ? 'page' : undefined}
                    onMouseEnter={showTip(label)}
                    onMouseLeave={hideTip}
                    onFocus={showTip(label)}
                    onBlur={hideTip}
                    className={[
                      'relative flex items-center gap-2.5 rounded-sm text-[14px] text-shell-on',
                      'transition-colors duration-micro ease-brand',
                      collapsed ? 'h-10 justify-center' : 'h-9 px-2.5',
                      active
                        ? 'bg-white/10 font-semibold'
                        : 'font-medium hover:bg-white/[0.06]',
                    ].join(' ')}
                  >
                    {active && (
                      <span
                        aria-hidden
                        className="absolute -left-2.5 bottom-2 top-2 w-[3px] rounded-r-[3px] bg-kumkum-500"
                      />
                    )}
                    <Symbol
                      name={icon}
                      size={20}
                      fill={active}
                      grade={active ? 50 : 0}
                      className={active ? 'text-white' : 'text-shell-on/90'}
                    />
                    {!collapsed && <span className="min-w-0 flex-1 truncate">{label}</span>}
                    {count !== undefined && count > 0 && (
                      <span
                        className={[
                          'count !bg-kumkum-500',
                          collapsed ? 'absolute right-1 top-0.5 !h-4 !min-w-4 !px-1 !text-[10px]' : '',
                        ].join(' ')}
                      >
                        {count}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {session && (
        <div
          className={[
            'mx-2.5 flex shrink-0 items-center gap-2.5 border-t border-white/[0.08] pb-4 pt-3',
            collapsed ? 'flex-col' : 'px-2',
          ].join(' ')}
        >
          <div
            className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-white/[0.12] text-[13.5px] font-bold text-white"
            title={collapsed ? prettyRole(session.role) : undefined}
          >
            {initialsFromRole(session.role)}
          </div>
          {collapsed ? (
            <button
              type="button"
              onClick={onLogout}
              aria-label="Sign out"
              onMouseEnter={showTip('Sign out')}
              onMouseLeave={hideTip}
              onFocus={showTip('Sign out')}
              onBlur={hideTip}
              className="grid h-9 w-9 place-items-center rounded-sm text-shell-muted transition-colors hover:bg-white/[0.06] hover:text-shell-on"
            >
              <Symbol name="logout" size={18} />
            </button>
          ) : (
            <div className="min-w-0 flex-1">
              <div className="truncate text-[13.5px] font-semibold leading-tight text-shell-on">
                {prettyRole(session.role)}
              </div>
              <button
                type="button"
                onClick={onLogout}
                className="text-[11.5px] leading-tight text-shell-muted transition-colors hover:text-shell-on hover:underline"
              >
                Sign out
              </button>
            </div>
          )}
        </div>
      )}

      {collapsed && tip && (
        <div
          role="tooltip"
          className="pointer-events-none fixed left-[76px] z-50 -translate-y-1/2 whitespace-nowrap rounded-[6px] bg-ink-900 px-2.5 py-1.5 text-[12.5px] font-medium text-white shadow-elev-2"
          style={{ top: tip.top }}
        >
          <span
            aria-hidden
            className="absolute -left-[5px] top-1/2 -translate-y-1/2 border-y-[5px] border-r-[5px] border-y-transparent border-r-ink-900"
          />
          {tip.label}
        </div>
      )}
    </>
  );
}
